import { all, debounce, put, call, race, select, take, takeLatest } from 'redux-saga/effects'

// Redux
import * as types from 'Redux/Types/UserManagementTypes'
import * as actions from 'Redux/Actions/UserManagementActions'
import selectors from 'Redux/Selectors'

// Services
import * as api from 'Service/Api'

function* addUserWorker({ payload }) {
  try {
    const { companyID } = yield select(selectors.getSelectedPersona)
    yield call(api.addManagedUser, { ...payload, companyId: companyID })
    yield put(actions.fetchManagedUsers({ companyID }))
    const [error] = yield race([
      take(types.FETCH_MANAGED_USERS_FAILED),
      take(types.FETCH_MANAGED_USERS_SUCCEEDED),
    ])
    if (error) {
      throw error
    }
    yield put(actions.addUserSucceeded())
  } catch (error) {
    yield put(actions.addUserFailed(JSON.parse(error.message)?.code))
  }
}

function* deactivateUsersWorker({ payload }) {
  try {
    const { companyID } = yield select(selectors.getSelectedPersona)
    const requests = payload.map((personaID) => {
      return call(api.updateManagedUser, { companyID, personaID, status: 'inactive' })
    })
    yield all(requests)
    yield put(actions.fetchManagedUsers({ companyID }))
    const [error] = yield race([
      take(types.FETCH_MANAGED_USERS_FAILED),
      take(types.FETCH_MANAGED_USERS_SUCCEEDED),
    ])
    if (error) {
      throw error
    }
    yield put(actions.deactivateUsersSucceeded())
  } catch (error) {
    yield put(actions.deactivateUsersFailed(error))
  }
}

function* fetchManagedUsersWorker({ payload }) {
  try {
    const response = yield call(api.fetchManagedUsers, payload)
    yield put(actions.fetchManagedUsersSucceeded(response))
  } catch (errors) {
    yield put(actions.fetchManagedUsersFailed(errors))
  }
}

function* fetchUserRolesWorker() {
  try {
    const response = yield call(api.fetchUserRoles)
    yield put(actions.fetchUserRolesSucceeded(response.data))
  } catch (error) {
    yield put(actions.fetchUserRolesFailed(error))
  }
}

function* registerManagedUserWorker({ payload }) {
  try {
    const { companyId, userId, ...formValues } = payload
    const { data } = yield call(api.getAuthToken, payload.email, payload.token)
    const response = yield call(api.registerManagedUser, companyId, userId, data.token, formValues)
    if (!response.ok) {
      throw new Error()
    }
    yield put(actions.registerManagedUserSucceeded())
  } catch (error) {
    console.log({ error })
    yield put(actions.registerManagedUserFailed(error))
  }
}

function* resendUserInviteWorker({ payload }) {
  try {
    const { companyID, company } = yield select(selectors.getSelectedPersona)
    yield call(api.addManagedUser, { companyName: company, companyId: companyID, ...payload })
    yield put(actions.fetchManagedUsers({ companyID }))
    const [error] = yield race([
      take(types.FETCH_MANAGED_USERS_FAILED),
      take(types.FETCH_MANAGED_USERS_SUCCEEDED),
    ])
    if (error) {
      throw error
    }
    yield put(actions.resendUserInviteSucceeded())
  } catch (error) {
    yield put(actions.resendUserInviteFailed(JSON.parse(error.message)?.code))
  }
}

function* updateManagedUserWorker({ payload }) {
  try {
    const { companyID } = yield select(selectors.getSelectedPersona)
    yield call(api.updateManagedUser, payload)
    yield put(actions.fetchManagedUsers({ companyID }))
    const [error] = yield race([
      take(types.FETCH_MANAGED_USERS_FAILED),
      take(types.FETCH_MANAGED_USERS_SUCCEEDED),
    ])
    if (error) {
      throw error
    }
    yield put(actions.updateManagedUserSucceeded())
  } catch (error) {
    yield put(actions.updateManagedUserFailed(error))
  }
}

export default function* userManagementSaga() {
  yield takeLatest(types.ADD_USER, addUserWorker)
  yield takeLatest(types.DEACTIVATE_USERS, deactivateUsersWorker)
  yield debounce(150, types.FETCH_MANAGED_USERS, fetchManagedUsersWorker)
  yield takeLatest(types.FETCH_USER_ROLES, fetchUserRolesWorker)
  yield takeLatest(types.REGISTER_MANAGED_USER, registerManagedUserWorker)
  yield takeLatest(types.RESEND_USER_INVITE, resendUserInviteWorker)
  yield takeLatest(types.UPDATE_MANAGED_USER, updateManagedUserWorker)
}
